import React, { useEffect, useMemo } from 'react';
import { FormField, Multiselect } from '@amzn/awsui-components-console';
import { Controller, useFormContext } from 'react-hook-form';
import { get } from 'lodash-es';
import {
	FROM_DIFF_STYLE,
	TO_DIFF_STYLE,
	ViewContent,
	useEditOnChange,
	useIsEditContentDisabled,
	useViewContent,
} from './contentHelpers';
import { DisplayMode, ResourcePath, ResourceType, iSelectContent } from './contentInterfaces';
import { LabeledContentSkeleton } from '../skeletons/LabeledContentSkeleton';
import { OptionsObj, optionsToObj } from 'src/commons';
import { HelpPanelInfoLink } from '../info/HelpPanelInfoLink';
import { DataAttributes, filterDataAttributes, getContentDataAttributes } from 'src/commons/dataAttributes';
import { ContentErrorWrapper } from 'src/components/error/ContentErrorBoundary';
import { Rules } from '.';
import { LabeledContent } from '../LabeledContent';

/** Renders the field identified by the `path` as a Polaris `<Multiselect>`. Manipulates the value selected options' values as a `string[]` type.
 * For an overview of `<Content>` components, see the [README.md](./README.md). For usage examples, see the [How-to guide](./HowToGuide.md) */
export const MultiselectContent = ContentErrorWrapper(_MultiselectContent);

const defaultMultiselectViewTransform = (values: any[]) => values.join(', ');

function _MultiselectContent<RType extends keyof ResourceType, DisabledOnPaths extends ResourcePath<RType>[]>(
	props: iSelectContent<RType, DisabledOnPaths>
) {
	// Processed at top level component to avoid unneccessary executions of a relatively heavy functions between renders
	const dataAttributes = { ...filterDataAttributes(props), ...getContentDataAttributes('Multiselect', props) };
	// Convert options list to a value-indexed object in order to efficiently retrieve the label corresponding to a value
	const optionsObj = useMemo(() => optionsToObj(props.options), [props.options]);

	const viewTransform = (values: string[]) => {
		// Maps the underlying (backend) values to their respective frontend labels using the provided options object, if available
		const mappedValues = (values ?? []).map((value) => optionsObj[value]?.label ?? value);
		// Applies the provided viewTransform, or defaults to joining values with commas
		return (props.viewTransform ?? defaultMultiselectViewTransform)(mappedValues);
	};

	switch (props.mode) {
		case DisplayMode.Diff:
			return (
				<DiffMultiselectContent
					{...props}
					dataAttributes={dataAttributes}
					viewTransform={viewTransform}
					optionsObj={optionsObj}
				/>
			);
		case DisplayMode.View:
			return <ViewContent {...props} dataAttributes={dataAttributes} viewTransform={viewTransform} />;
		case DisplayMode.Edit:
			return (
				<EditMultiselectContent
					{...props}
					optionsObj={optionsObj}
					dataAttributes={dataAttributes}
					viewTransform={viewTransform}
				/>
			);
		case DisplayMode.Loading:
			return <LabeledContentSkeleton label={props.label} />;
		default:
			return null;
	}
}

function EditMultiselectContent<RType extends keyof ResourceType, DisabledOnPaths extends ResourcePath<RType>[]>(
	props: iSelectContent<RType, DisabledOnPaths> & { optionsObj: OptionsObj; dataAttributes: DataAttributes }
) {
	const { control, clearErrors, setValue } = useFormContext<ResourceType[RType]>();
	const disabled = useIsEditContentDisabled(props);

	return (
		<Controller
			control={control}
			name={props.path}
			rules={Rules.useRules(props.rules, props.label, disabled)}
			render={({ field, formState, fieldState }) => {
				const onChange = useEditOnChange(props, field.onChange, setValue, 'options');

				useEffect(() => {
					// This effect will ignore any potential validation errors if the particular field is disabled
					if (disabled) {
						clearErrors(props.path);
					}
				}, [disabled, formState.isSubmitting, formState.isValidating]);

				return (
					<FormField
						label={props.label}
						description={props.editDescription}
						errorText={get(formState, `errors.${props.path}.message`)}
						info={props.infoHelpPanel && <HelpPanelInfoLink helpPanel={props.infoHelpPanel} />}
					>
						<span {...props.dataAttributes}>
							<Multiselect
								// General props
								ref={field.ref}
								onChange={onChange}
								onBlur={field.onBlur}
								invalid={fieldState.invalid}
								disabled={disabled}
								// Multiselect-specific props:
								placeholder={
									props.placeholder ??
									(props.options?.length > 0 ? 'Select options ...' : 'No options found.')
								}
								options={props.options}
								selectedOptions={((field.value as string[]) ?? []).map(
									(value: string) => props.optionsObj[value] ?? { value, label: value }
								)}
								selectedAriaLabel="Selected"
								// These props facilitate asynchronous options loading, which is especially useful for large dropdowns (e.g. CTI)
								filteringType={props.filteringType}
								onLoadItems={props.onLoadItems}
								statusType={props.statusType}
								loadingText={props.loadingText}
								errorText={props.errorText}
								finishedText={props.finishedText}
							/>
						</span>
					</FormField>
				);
			}}
		/>
	);
}

export function DiffMultiselectContent<RType extends keyof ResourceType, DisabledOnPaths extends ResourcePath<RType>[]>(
	props: iSelectContent<RType, DisabledOnPaths> & { optionsObj: OptionsObj; dataAttributes: DataAttributes }
) {
	const { value: rawFromValue } = useViewContent(props);
	const { value: rawToValue } = useViewContent({ ...props, resourceId: props.diffResourceId });

	// Default to empty list and map values to frontend labels, if available
	const fromValues: any[] = (rawFromValue ?? []).map((value) => props.optionsObj[value]?.label ?? value);
	const toValues: any[] = (rawToValue ?? []).map((value) => props.optionsObj[value]?.label ?? value);

	const fromValuesSet = new Set(fromValues);
	const toValuesSet = new Set(toValues);
	const isEqual = fromValuesSet.size === toValuesSet.size && [...fromValues].every((value) => toValuesSet.has(value));

	return (
		<LabeledContent
			label={props.label}
			missingText={props.missingText}
			info={props.infoHelpPanel && <HelpPanelInfoLink helpPanel={props.infoHelpPanel} />}
		>
			<span {...props.dataAttributes}>
				<div>
					{fromValues
						.map((value) => [
							<span style={!isEqual && !toValuesSet.has(value) ? FROM_DIFF_STYLE : {}}>{value}</span>,
							// Comma-separates the spans
							', ',
						])
						// Flattens the list-of-lists, thereby joining the spans with commas
						.flatMap((x) => x)
						// Removes an trailing comma
						.slice(0, -1)}
				</div>
				{!isEqual && <div> ↓ </div>}
				{!isEqual &&
					toValues
						.map((value) => [
							<span style={!fromValuesSet.has(value) ? TO_DIFF_STYLE : {}}>{value}</span>,
							// Comma-separates the spans
							', ',
						])
						// Flattens the list-of-lists, thereby joining the spans with commas
						.flatMap((x) => x)
						// Removes an trailing comma
						.slice(0, -1)}
			</span>
		</LabeledContent>
	);
}
